import React, { useState } from 'react';
import i18n from '@nextroll/ar-i18n';

import { InputGroup } from '@nextroll/ar-style-base';
import { getValidationError, isValidPropertyValue, ValidationError } from 'util/errors';
import { DefinitionInputPropertyFieldBase } from 'types/propertyGeneration';
import { PropertyType, PropertyValidation } from 'types/roflWorkflowActionProperty';
import { InputErrorElement, InputType } from 'components/basic/inputProperty/InputErrorElement/InputErrorElement';

/**
 *  TextInputDefinitionProps are the type of props needed to update a workflow from this component
 */
export interface TextInputDefinitionUpdateWorkflowParams {
    value: string;
}

/**
 *  TextInputDefinitionProps represent the props for the TextInputDefinition component
 */
export interface TextInputDefinitionProps extends
    DefinitionInputPropertyFieldBase<TextInputDefinitionUpdateWorkflowParams> {
}

/**
 *  This component represents a TextInputDefinition where a user enters text,
 *  that text is retained on the "blur" event, and the value is eventually saved on the current workflow
 */
export const TextInputDefinition = (props: TextInputDefinitionProps) => {
    const [userValue, setUserValue] = useState<string>(props.workflowConfig?.[props.inputProperty.name]?.[props.field.name] ?? '');
    const [error, setError] = React.useState<React.ReactNode>();
    const [isUserValueUpdating, setIsUserValueUpdating] = useState<boolean>();
    const fieldDisplayName = props.field?.display_name || '';
    const onValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsUserValueUpdating(true);
        setUserValue(e.target.value);
    };

    const { identifier, hideLabel, setIsValid, propertyError, serviceTitle } = props;

    return (
        <div className='text-input-definition'>
            {!hideLabel && (
                <p className='entity-title'>
                    {props.field.display_name}
                </p>
            )}
            <InputGroup
                i18n={i18n.noop}
                classNames='text-input-definition-input-group'
                validationState={error ? InputGroup.VALIDATION_ERROR : undefined}
                inputProps={{
                    placeholder: i18n.gettext('Enter Text'),
                    onChange: onValueChange,
                    onBlur: async () => {
                        // return immediately if we havent updated anything
                        if (!userValue) {
                            return;
                        }

                        // quick validation
                        if (props.field.type === PropertyType.INT
                            && !isValidPropertyValue(userValue, PropertyValidation.INT)) {
                            setError(getValidationError(ValidationError.INVALID_INPUT, fieldDisplayName));
                        } else if (props.field.validation && !isValidPropertyValue(userValue, props.field.validation)) {
                            setError(getValidationError(ValidationError.INVALID_INPUT, fieldDisplayName));
                        } else {
                            props.updateWorkflow({
                                value: userValue,
                            });
                            setIsUserValueUpdating(false);
                            setError(undefined);
                        }
                    },
                    value: userValue,
                }}
            />
            <InputErrorElement
                value={userValue}
                inputType={InputType.TEXT}
                error={error}
                propertyError={propertyError}
                setError={setError}
                setIsValid={setIsValid}
                fieldIsValid={!!userValue && !isUserValueUpdating}
                errorClassName='text-input-definition-error-msg'
                identifier={identifier}
                entityTitle={fieldDisplayName}
                serviceTitle={serviceTitle}
            />
        </div>
    );
};
